// components/prediction/SettlementModal.jsx
// Next-day settlement popup showing yesterday's prediction results

import React from 'react';
import { theme } from '../../theme';

const TIER_LABELS = {
  'big_rise': 'Big Rise',
  'small_rise': 'Small Rise',
  'flat': 'Flat',
  'small_drop': 'Small Drop',
  'big_drop': 'Big Drop',
};

const TIER_COLORS = {
  'big_rise': '#2dd4bf',
  'small_rise': '#22c55e',
  'flat': '#8b949e',
  'small_drop': '#f0883e',
  'big_drop': '#f85149',
};

export function SettlementModal({ visible, onClose, settlement }) {
  if (!visible || !settlement) return null;

  const { ticker, date, actualChange, actualTier, userTier, aiTier, userCorrect, aiCorrect,
          oldIQ, newIQ, oldStreak, newStreak, rank } = settlement;

  const iqDiff = newIQ - oldIQ;
  const streakBroke = newStreak === 0 && oldStreak > 0;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.75)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    }}>
      <div style={{
        background: theme.bgCard, borderRadius: '20px', padding: '40px',
        maxWidth: '560px', width: '90%', border: '1px solid ' + theme.border,
        position: 'relative',
      }}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: '16px', right: '16px',
            background: 'transparent', border: 'none', color: theme.textDark,
            fontSize: '24px', cursor: 'pointer', padding: '4px 8px',
          }}
        >×</button>

        {/* Title */}
        <h2 style={{ color: theme.text, fontSize: '24px', fontWeight: 600, margin: '0 0 6px', textAlign: 'center' }}>
          Yesterday's Result — {ticker}
        </h2>
        <p style={{ color: theme.textDark, fontSize: '14px', margin: '0 0 24px', textAlign: 'center' }}>{date}</p>

        {/* Result Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '24px' }}>
          {/* Actual */}
          <div style={{ background: theme.bg, borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
            <p style={{ color: theme.textDark, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px' }}>
              Actual Result
            </p>
            <p style={{ color: TIER_COLORS[actualTier] || theme.text, fontSize: '16px', fontWeight: 700, margin: 0 }}>
              {TIER_LABELS[actualTier]}
            </p>
            <p style={{ color: actualChange >= 0 ? '#22c55e' : '#f85149', fontSize: '14px', margin: '4px 0 0' }}>
              {actualChange >= 0 ? '+' : ''}{actualChange}%
            </p>
          </div>

          {/* User */}
          <div style={{
            background: theme.bg, borderRadius: '12px', padding: '16px', textAlign: 'center',
            border: '2px solid ' + (userCorrect ? '#22c55e' : '#f85149'),
          }}>
            <p style={{ color: theme.textDark, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px' }}>
              Your Prediction
            </p>
            <p style={{ color: TIER_COLORS[userTier] || theme.text, fontSize: '16px', fontWeight: 700, margin: 0 }}>
              {TIER_LABELS[userTier]}
            </p>
            <p style={{ color: userCorrect ? '#22c55e' : '#f85149', fontSize: '18px', margin: '4px 0 0' }}>
              {userCorrect ? '✓ Correct' : '✗ Wrong'}
            </p>
          </div>

          {/* AI */}
          <div style={{
            background: theme.bg, borderRadius: '12px', padding: '16px', textAlign: 'center',
            border: '2px solid ' + (aiCorrect ? '#22c55e' : '#f85149'),
          }}>
            <p style={{ color: theme.textDark, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px' }}>
              AI Prediction
            </p>
            <p style={{ color: TIER_COLORS[aiTier] || theme.text, fontSize: '16px', fontWeight: 700, margin: 0 }}>
              {TIER_LABELS[aiTier]}
            </p>
            <p style={{ color: aiCorrect ? '#22c55e' : '#f85149', fontSize: '18px', margin: '4px 0 0' }}>
              {aiCorrect ? '✓ Correct' : '✗ Wrong'}
            </p>
          </div>
        </div>

        {/* Score Changes */}
        <div style={{
          background: theme.bg, borderRadius: '12px', padding: '16px',
          display: 'flex', justifyContent: 'center', gap: '32px', marginBottom: '24px',
        }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: theme.textDark, fontSize: '11px', margin: '0 0 4px', textTransform: 'uppercase' }}>IQ Score</p>
            <p style={{ fontSize: '16px', margin: 0 }}>
              <span style={{ color: theme.textMuted }}>{oldIQ}</span>
              <span style={{ color: theme.textDark }}> → </span>
              <span style={{ color: iqDiff >= 0 ? '#22c55e' : '#f85149', fontWeight: 700 }}>
                {newIQ} ({iqDiff >= 0 ? '+' : ''}{iqDiff})
              </span>
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: theme.textDark, fontSize: '11px', margin: '0 0 4px', textTransform: 'uppercase' }}>Streak</p>
            <p style={{ fontSize: '16px', margin: 0 }}>
              <span style={{ color: theme.textMuted }}>{oldStreak}</span>
              <span style={{ color: theme.textDark }}> → </span>
              <span style={{ color: streakBroke ? '#f85149' : '#22c55e', fontWeight: 700 }}>
                {newStreak} {streakBroke ? '(broken)' : ''}
              </span>
            </p>
          </div>
          {rank && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: theme.textDark, fontSize: '11px', margin: '0 0 4px', textTransform: 'uppercase' }}>Rank</p>
              <p style={{ fontSize: '16px', fontWeight: 700, color: theme.textMuted, margin: 0 }}>{rank}</p>
            </div>
          )}
        </div>

        {/* CTA */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={onClose}
            style={{
              padding: '14px 40px', borderRadius: '10px', border: 'none',
              background: theme.teal, color: '#0f172a', fontSize: '16px',
              fontWeight: 700, cursor: 'pointer',
            }}
          >
            Make Today's Prediction →
          </button>
        </div>
      </div>
    </div>
  );
}

export default SettlementModal;
